// <pos-customer-reservation/> — 顧客訂位頁（手機優先）
// 路徑：#/reserve/:slug
// 流程：日期 + 時段 + 人數 + 姓名電話 → 送單 → 顯示確認
// MVP：不查時段可用度，由 reservation_config min_advance_minutes / max_advance_days 限制
// see netlify/functions/reservation-create.js

import { fetchPublicMenu, createPublicReservation, fetchReservationAvailability } from '../lib/api.js';
import { t, lang, setLang, langToggleHtml, LANG_TOGGLE_CSS } from '../lib/i18n.js';

function parseSlug() {
  const m = location.hash.match(/^#\/reserve\/([^\/]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

function pad2(n) { return String(n).padStart(2, '0'); }

// 產出未來 7 天的日期選項（YYYY-MM-DD）
function dateOptions(timezone = 'Asia/Taipei') {
  const opts = [];
  const now = new Date();
  for (let i = 0; i < 7; i++) {
    const d = new Date(now.getTime() + i * 86_400_000);
    // Asia/Taipei 偏移簡化（與其他模組一致）
    const tpOffset = 8 * 60 * 60 * 1000;
    const tp = new Date(d.getTime() + tpOffset);
    const ymd = `${tp.getUTCFullYear()}-${pad2(tp.getUTCMonth() + 1)}-${pad2(tp.getUTCDate())}`;
    const wdName = t(['週日', '週一', '週二', '週三', '週四', '週五', '週六'][tp.getUTCDay()]);
    const label = i === 0 ? t('今天') : i === 1 ? t('明天') : `${tp.getUTCMonth() + 1}/${tp.getUTCDate()} (${wdName})`;
    opts.push({ value: ymd, label });
  }
  return opts;
}

// 產出時段（11:00 ~ 21:00 每 30min，MVP 硬寫；W9 正式週改讀 reservation_slots）
function slotOptions() {
  const slots = [];
  for (let h = 11; h <= 21; h++) {
    for (const m of [0, 30]) {
      if (h === 21 && m === 30) continue;
      slots.push(`${pad2(h)}:${pad2(m)}`);
    }
  }
  return slots;
}

// 台北「今天」YYYY-MM-DD
function taipeiTodayYmd() {
  const tp = new Date(Date.now() + 8 * 3600_000);
  return `${tp.getUTCFullYear()}-${pad2(tp.getUTCMonth() + 1)}-${pad2(tp.getUTCDate())}`;
}

/** 月曆格子：該月 1 號前置空格 + 每日 {ymd, day, weekday, disabled} */
function calendarCells(monthYm, todayYmd, maxYmd, openWeekdays) {
  const [y, m] = monthYm.split('-').map(Number);
  const firstWd = new Date(Date.UTC(y, m - 1, 1)).getUTCDay();
  const daysInMonth = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const cells = Array.from({ length: firstWd }, () => null);
  for (let d = 1; d <= daysInMonth; d++) {
    const ymd = `${y}-${pad2(m)}-${pad2(d)}`;
    const weekday = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
    const disabled = ymd < todayYmd || ymd > maxYmd
      || (openWeekdays && !openWeekdays.includes(weekday));
    cells.push({ ymd, day: d, weekday, disabled });
  }
  return cells;
}

/** 最短提前（店家設定，day 粒度）：min_advance_minutes ≥ 1 天 → 最早可訂日往後推 */
function minSelectableYmd(todayYmd, minAdvanceMinutes) {
  const days = Math.floor((minAdvanceMinutes || 0) / 1440);
  return days > 0 ? addDaysYmd(todayYmd, days) : todayYmd;
}

function addDaysYmd(ymd, n) {
  const [y, m, d] = ymd.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, d) + n * 86_400_000);
  return `${t.getUTCFullYear()}-${pad2(t.getUTCMonth() + 1)}-${pad2(t.getUTCDate())}`;
}

class PosCustomerReservation extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.state = {
      loading: true,
      submitting: false,
      error: null,
      tenant: null,
      // form
      date: '',
      time: '',
      partySize: 2,
      guestName: '',
      guestPhone: '',
      guestEmail: '',
      note: '',
      // availability
      availability: null,    // { has_seats_configured, total_seats, slots: [{time, available, remaining}] }
      checkingAvailability: false,
      // 訂位開放設定（月曆用）：max_advance_days / open_weekdays
      resvConfig: null,
      calMonth: '',          // 目前顯示月份 YYYY-MM
      // result
      success: null,
    };
  }

  async connectedCallback() {
    const dates = dateOptions();
    this.state.date = dates[0]?.value || '';
    this.state.calMonth = this.state.date.slice(0, 7);
    this.state.time = '18:00';
    this.render();
    await this.load();
  }

  async loadResvConfig() {
    const slug = parseSlug();
    if (!slug) return;
    try {
      const data = await fetchReservationAvailability({ slug, partySize: this.state.partySize });
      this.state.resvConfig = data.config || null;
      this.render();
    } catch { /* 拿不到 config 用預設 30 天 */ }
  }

  async load() {
    const slug = parseSlug();
    if (!slug) {
      this.state.loading = false;
      this.state.error = t('網址格式錯誤');
      this.render();
      return;
    }
    try {
      // 重用 public-menu endpoint 拿 tenant 名（MVP 偷懶，不另寫 endpoint）
      const data = await fetchPublicMenu(slug);
      this.state.tenant = data.tenant;
    } catch (err) {
      this.state.error = (lang() === 'zh' ? err.message_zh : err.message_en) || err.message || t('無法載入店家資訊');
    } finally {
      this.state.loading = false;
      this.render();
    }
    // 載入完馬上算當前 date + party_size 的 availability + 訂位開放設定
    this.loadResvConfig();
    this.refreshAvailability();
  }

  async refreshAvailability() {
    const slug = parseSlug();
    if (!slug || !this.state.date) return;
    this.state.checkingAvailability = true;
    this.render();
    try {
      const data = await fetchReservationAvailability({
        slug,
        date: this.state.date,
        partySize: this.state.partySize,
      });
      this.state.availability = data;
      if (data.config) this.state.resvConfig = data.config;
      // 若選的時段被改成滿桌 → 清空時段，逼用戶重選
      if (this.state.time && data.has_seats_configured) {
        const slot = (data.slots || []).find((s) => s.time === this.state.time);
        if (slot && !slot.available) {
          this.state.time = '';
        }
      }
    } catch (err) {
      console.warn('[reservation] availability check failed', err);
      this.state.availability = null;  // 失敗時不阻塞，全部 chip 可選
    } finally {
      this.state.checkingAvailability = false;
      this.render();
    }
  }

  reservedAtISO() {
    // 把 date + time（Asia/Taipei）→ ISO timestamp（UTC）
    if (!this.state.date || !this.state.time) return null;
    const [y, m, d] = this.state.date.split('-').map(Number);
    const [h, mi] = this.state.time.split(':').map(Number);
    // local Taipei → UTC
    const utcMs = Date.UTC(y, m - 1, d, h, mi) - 8 * 60 * 60 * 1000;
    return new Date(utcMs).toISOString();
  }

  async submit() {
    if (this.state.submitting) return;
    if (!this.state.guestName.trim()) {
      this.state.error = t('請填寫姓名');
      this.render();
      return;
    }
    if (!/^[0-9+\-\s]{6,20}$/.test(this.state.guestPhone.trim())) {
      this.state.error = t('電話格式錯誤');
      this.render();
      return;
    }
    const email = this.state.guestEmail.trim();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.state.error = t('Email 格式錯誤');
      this.render();
      return;
    }
    const isoTs = this.reservedAtISO();
    if (!isoTs) {
      this.state.error = t('請選日期與時段');
      this.render();
      return;
    }

    this.state.submitting = true;
    this.state.error = null;
    this.render();

    try {
      const slug = parseSlug();
      const result = await createPublicReservation({
        slug,
        reservedAt: isoTs,
        partySize: this.state.partySize,
        guestName: this.state.guestName.trim(),
        guestPhone: this.state.guestPhone.trim(),
        guestEmail: this.state.guestEmail.trim() || undefined,
        note: this.state.note.trim() || undefined,
      });
      this.state.success = result.reservation;
      this.state.manageUrl = result.manage_url || null;
    } catch (err) {
      this.state.error = (lang() === 'zh' ? err.message_zh : err.message_en) || err.message || t('送出失敗，請重試');
    } finally {
      this.state.submitting = false;
      this.render();
    }
  }

  render() {
    const { loading, error, tenant, date, time, partySize, guestName, guestPhone, note, submitting, success, availability, checkingAvailability } = this.state;
    const dates = dateOptions();
    const slots = slotOptions();
    const slotMap = new Map((availability?.slots || []).map((s) => [s.time, s]));
    const noSeatsConfigured = availability && !availability.has_seats_configured;

    if (loading) {
      this.shadowRoot.innerHTML = `<style>${this.styles()}</style><div class="msg">${t('載入中…')}</div>`;
      return;
    }

    if (success) {
      const dt = new Date(success.reserved_at);
      const dtLabel = `${dt.getMonth() + 1}/${dt.getDate()} ${pad2(dt.getHours())}:${pad2(dt.getMinutes())}`;
      this.shadowRoot.innerHTML = `
        <style>${this.styles()}</style>
        <div class="success">
          <div class="success__icon">✓</div>
          <h2>${t('訂位已確認')}</h2>
          <div class="success__row"><span>${t('店家')}</span><strong>${escapeHtml(tenant?.display_name || '')}</strong></div>
          <div class="success__row"><span>${t('時間')}</span><strong>${dtLabel}</strong></div>
          <div class="success__row"><span>${t('人數')}</span><strong>${t('{n} 位', { n: success.party_size })}</strong></div>
          <div class="success__row"><span>${t('姓名')}</span><strong>${escapeHtml(success.guest_name)}</strong></div>
          <div class="policy" style="margin-top:1rem">⏱ ${t('訂位保留 10 分鐘，逾時恕不保留座位')}</div>
          ${this.state.manageUrl ? `
            <a class="manage-link" href="${this.state.manageUrl}">${t('管理訂位（確認出席 / 取消）')}</a>
            ${this.state.guestEmail.trim() ? `<div class="success__hint">${t('確認信已寄到你的 Email，內含管理連結')}</div>` : `<div class="success__hint">${t('建議截圖保存此頁，以便需要時取消')}</div>`}
          ` : `<div class="success__hint">${t('如需更改或取消，請直接致電店家')}</div>`}
        </div>
      `;
      return;
    }

    this.shadowRoot.innerHTML = `
      <style>${this.styles()}</style>
      <header class="hd" style="position: relative;">
        <div class="hd__name">${escapeHtml(tenant?.display_name || t('店家'))}</div>
        <div class="hd__sub">${t('線上訂位')}</div>
        ${langToggleHtml()}
      </header>

      <main class="form">
        ${error ? `<div class="alert">${escapeHtml(error)}</div>` : ''}

        <label class="lbl">${t('日期')}</label>
        ${(() => {
          const todayYmd0 = taipeiTodayYmd();
          const maxDays = this.state.resvConfig?.max_advance_days || 30;
          const openWd = this.state.resvConfig?.open_weekdays || null;
          const todayYmd = minSelectableYmd(todayYmd0, this.state.resvConfig?.min_advance_minutes);
          const maxYmd = addDaysYmd(todayYmd0, maxDays);
          const ym = this.state.calMonth || todayYmd.slice(0, 7);
          const cells = calendarCells(ym, todayYmd, maxYmd, openWd);
          const [cy, cm] = ym.split('-').map(Number);
          const prevYm = `${cm === 1 ? cy - 1 : cy}-${pad2(cm === 1 ? 12 : cm - 1)}`;
          const nextYm = `${cm === 12 ? cy + 1 : cy}-${pad2(cm === 12 ? 1 : cm + 1)}`;
          const canPrev = ym > todayYmd.slice(0, 7);
          const canNext = nextYm <= maxYmd.slice(0, 7);
          return `
          <div class="cal">
            <div class="cal__head">
              <button type="button" class="cal__nav" data-cal="${prevYm}" ${canPrev ? '' : 'disabled'}>‹</button>
              <span class="cal__title">${cy} ${t('年')} ${cm} ${t('月')}</span>
              <button type="button" class="cal__nav" data-cal="${nextYm}" ${canNext ? '' : 'disabled'}>›</button>
            </div>
            <div class="cal__grid">
              ${[t('日'), t('一'), t('二'), t('三'), t('四'), t('五'), t('六')].map((w) => `<span class="cal__wd">${w}</span>`).join('')}
              ${cells.map((c) => c === null ? '<span></span>' : `
                <button type="button" class="cal__day ${date === c.ymd ? 'is-on' : ''}"
                  data-date="${c.ymd}" ${c.disabled ? 'disabled' : ''}>${c.day}</button>`).join('')}
            </div>
            <div class="cal__hint">${t('可預約至')} ${maxYmd.replace(/-/g, '/')}</div>
          </div>`;
        })()}

        <label class="lbl">
          ${t('時段')}
          ${checkingAvailability ? `<span class="lbl-hint">${t('檢查中…')}</span>` : ''}
          ${noSeatsConfigured ? `<span class="lbl-hint lbl-hint--warn">${t('店家未設座位數，無法擋滿桌')}</span>` : ''}
        </label>
        ${availability && (availability.slots || []).length === 0 ? `
          <div class="alert">${t('本日公休，請選其他日期')}</div>` : ''}
        <div class="chips chips--time" data-field="time">
          ${(availability?.slots ? availability.slots.map((x) => x.time) : slots).map((s) => {
            const info = slotMap.get(s);
            const full = info && !info.available;
            const remaining = info?.remaining;
            return `
              <button type="button"
                class="chip ${time === s ? 'is-on' : ''} ${full ? 'is-full' : ''}"
                data-val="${s}"
                ${full ? 'disabled aria-disabled="true"' : ''}
                title="${full ? t('此時段已滿') : (remaining != null ? t('剩 {n} 位', { n: remaining }) : '')}"
              >${s}${full ? ` · ${t('滿')}` : ''}</button>
            `;
          }).join('')}
        </div>

        <label class="lbl">${t('人數')}</label>
        <div class="counter">
          <button type="button" class="cnt-btn" data-act="party-minus" ${partySize <= 1 ? 'disabled' : ''}>−</button>
          <span class="cnt-val">${t('{n} 位', { n: partySize })}</span>
          <button type="button" class="cnt-btn" data-act="party-plus" ${partySize >= 20 ? 'disabled' : ''}>+</button>
        </div>

        <label class="lbl" for="rname">${t('姓名')}</label>
        <input id="rname" class="inp" maxlength="100" value="${escapeHtml(guestName)}" data-field="guestName" />

        <label class="lbl" for="rphone">${t('電話')}</label>
        <input id="rphone" class="inp" type="tel" maxlength="20" value="${escapeHtml(guestPhone)}" data-field="guestPhone" placeholder="0912-345-678" />

        <label class="lbl" for="remail">${t('Email（選填，收訂位確認與前一天提醒）')}</label>
        <input id="remail" class="inp" type="email" maxlength="120" value="${escapeHtml(this.state.guestEmail)}" data-field="guestEmail" placeholder="you@example.com" />

        <label class="lbl" for="rnote">${t('備註（選填）')}</label>
        <textarea id="rnote" class="inp inp--area" maxlength="500" data-field="note">${escapeHtml(note)}</textarea>

        <div class="policy">⏱ ${t('訂位保留 10 分鐘，逾時恕不保留座位')}</div>

        <button type="button" class="btn-submit" ${submitting ? 'disabled' : ''} data-act="submit">
          ${submitting ? t('送出中…') : t('確認訂位')}
        </button>
      </main>
    `;

    this.bind();
  }

  bind() {
    const root = this.shadowRoot;

    root.querySelectorAll('.cal__nav[data-cal]').forEach((b) => {
      b.addEventListener('click', () => {
        this.state.calMonth = b.dataset.cal;
        this.render();
      });
    });
    root.querySelectorAll('.cal__day[data-date]').forEach((b) => {
      b.addEventListener('click', () => {
        this.state.date = b.dataset.date;
        this.state.time = '';
        this.render();
        this.refreshAvailability();
      });
    });
    root.querySelectorAll('.chips[data-field="date"] .chip').forEach((b) => {
      b.addEventListener('click', () => {
        this.state.date = b.dataset.val;
        this.state.error = null;
        this.render();
        this.refreshAvailability();
      });
    });
    root.querySelectorAll('.chips[data-field="time"] .chip').forEach((b) => {
      b.addEventListener('click', () => {
        if (b.disabled) return;  // 滿桌不可選
        this.state.time = b.dataset.val;
        this.state.error = null;
        this.render();
      });
    });

    const minusBtn = root.querySelector('[data-act="party-minus"]');
    if (minusBtn) minusBtn.addEventListener('click', () => {
      this.state.partySize = Math.max(1, this.state.partySize - 1);
      this.render();
      this.refreshAvailability();
    });
    const plusBtn = root.querySelector('[data-act="party-plus"]');
    if (plusBtn) plusBtn.addEventListener('click', () => {
      this.state.partySize = Math.min(20, this.state.partySize + 1);
      this.render();
      this.refreshAvailability();
    });

    const nameI = root.querySelector('input[data-field="guestName"]');
    if (nameI) nameI.addEventListener('input', (e) => { this.state.guestName = e.target.value; });
    const phoneI = root.querySelector('input[data-field="guestPhone"]');
    if (phoneI) phoneI.addEventListener('input', (e) => { this.state.guestPhone = e.target.value; });
    const emailI = root.querySelector('input[data-field="guestEmail"]');
    if (emailI) emailI.addEventListener('input', (e) => { this.state.guestEmail = e.target.value; });
    const noteI = root.querySelector('textarea[data-field="note"]');
    if (noteI) noteI.addEventListener('input', (e) => { this.state.note = e.target.value; });

    const langBtn = root.querySelector('[data-act="lang-toggle"]');
    if (langBtn) langBtn.addEventListener('click', () => {
      setLang(lang() === 'zh' ? 'en' : 'zh');
      this.render();
    });

    const submitBtn = root.querySelector('[data-act="submit"]');
    if (submitBtn) submitBtn.addEventListener('click', () => this.submit());
  }

  styles() {
    return `
      :host { display: block; min-height: 100vh; background: #faf8f4; font-family: -apple-system, BlinkMacSystemFont, 'PingFang TC', sans-serif; color: #2a2a2a; }
      .msg { padding: 4rem 1rem; text-align: center; color: #666; }
      .alert { background: #fff3cd; color: #856404; padding: 0.75rem 1rem; border-radius: 8px; margin-bottom: 1rem; font-size: 0.95rem; }
      .hd { padding: 1.25rem 1rem 1rem; background: #fff; border-bottom: 1px solid #eee; }
      ${LANG_TOGGLE_CSS}
      .hd__name { font-size: 1.25rem; font-weight: 600; }
      .hd__sub { color: #888; font-size: 0.9rem; margin-top: 0.25rem; }
      .form { padding: 1rem; max-width: 520px; margin: 0 auto; }
      .lbl { display: block; font-size: 0.9rem; color: #555; margin: 1rem 0 0.5rem; font-weight: 500; }
      .chips { display: flex; gap: 0.5rem; flex-wrap: wrap; }
      .chips--time { gap: 0.4rem; }
      .cal { background: #fff; border: 1px solid #eee; border-radius: 12px; padding: 0.75rem; }
      .cal__head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.5rem; }
      .cal__title { font-weight: 700; }
      .cal__nav { width: 2.2rem; height: 2.2rem; border: 1px solid #ddd; background: #fff; border-radius: 999px; font-size: 1.1rem; cursor: pointer; }
      .cal__nav:disabled { opacity: 0.3; cursor: not-allowed; }
      .cal__grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 4px; }
      .cal__wd { text-align: center; font-size: 0.75rem; color: #999; padding: 0.25rem 0; }
      .cal__day { aspect-ratio: 1; border: 1px solid transparent; background: #f7f5f2; border-radius: 10px; font-size: 0.95rem; cursor: pointer; }
      .cal__day:disabled { background: transparent; color: #ccc; cursor: not-allowed; }
      .cal__day.is-on { background: #c97b4f; color: #fff; font-weight: 700; }
      .cal__hint { margin-top: 0.5rem; font-size: 0.75rem; color: #999; text-align: right; }
      .chip { padding: 0.6rem 0.9rem; border: 1px solid #ddd; background: #fff; border-radius: 999px; font-size: 0.9rem; cursor: pointer; }
      .chip.is-on { background: #c97b4f; color: #fff; border-color: #c97b4f; }
      .chip.is-full { background: #f0f0f0; color: #aaa; border-color: #ddd; cursor: not-allowed; text-decoration: line-through; }
      .chip:disabled { cursor: not-allowed; }
      .lbl-hint { font-size: 0.8rem; color: #888; font-weight: 400; margin-left: 0.5rem; }
      .lbl-hint--warn { color: #c97b4f; }
      .counter { display: flex; align-items: center; gap: 1.5rem; padding: 0.5rem 0; }
      .cnt-btn { width: 44px; height: 44px; border-radius: 50%; border: 1px solid #ddd; background: #fff; font-size: 1.5rem; cursor: pointer; }
      .cnt-btn:disabled { opacity: 0.4; cursor: not-allowed; }
      .cnt-val { font-size: 1.05rem; font-weight: 500; }
      .inp { width: 100%; padding: 0.8rem 1rem; border: 1px solid #ddd; border-radius: 10px; font-size: 1rem; box-sizing: border-box; background: #fff; }
      .inp:focus { outline: none; border-color: #c97b4f; }
      .inp--area { min-height: 80px; resize: vertical; font-family: inherit; }
      .btn-submit { width: 100%; padding: 1rem; margin-top: 1.5rem; background: #c97b4f; color: #fff; border: 0; border-radius: 12px; font-size: 1.05rem; font-weight: 600; cursor: pointer; }
      .btn-submit:disabled { opacity: 0.6; cursor: wait; }
      .policy { margin-top: 1.25rem; padding: 0.65rem 0.9rem; background: #fdf2ea; color: #a15c2f; border-radius: 8px; font-size: 0.85rem; }
      .manage-link { display: inline-block; margin-top: 1rem; padding: 0.7rem 1.2rem; border: 1px solid #c97b4f; color: #c97b4f; border-radius: 10px; text-decoration: none; font-weight: 600; font-size: 0.95rem; }
      .success { padding: 3rem 1.5rem; max-width: 480px; margin: 0 auto; text-align: center; }
      .success__icon { width: 72px; height: 72px; border-radius: 50%; background: #c97b4f; color: #fff; display: inline-flex; align-items: center; justify-content: center; font-size: 2.4rem; margin-bottom: 1.25rem; }
      .success h2 { margin: 0 0 1.5rem; font-size: 1.4rem; }
      .success__row { display: flex; justify-content: space-between; padding: 0.75rem 0; border-bottom: 1px solid #eee; font-size: 0.95rem; }
      .success__row span { color: #888; }
      .success__hint { color: #888; font-size: 0.85rem; margin-top: 1.5rem; }
    `;
  }
}

if (!customElements.get('pos-customer-reservation')) {
  customElements.define('pos-customer-reservation', PosCustomerReservation);
}
